import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { authService } from '@/lib/auth/authService';
import { Home } from 'lucide-react';
import LoadingSpinner from '@/components/LoadingSpinner';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import DashboardNavigation from '@/components/dashboard/DashboardNavigation';
import DashboardOverview from '@/components/dashboard/DashboardOverview';
import UserMachines from '@/components/dashboard/UserMachines';
import SpareParts from '@/components/dashboard/SpareParts';
import AnalyticsDashboard from '@/components/dashboard/AnalyticsDashboard';
import NotificationCenter from '@/components/dashboard/NotificationCenter';
import DocumentationLibrary from '@/components/dashboard/DocumentationLibrary';
import SupportCenter from '@/components/dashboard/SupportCenter';
import { Button } from '@/components/ui/button';

// Import icons
import {
  LayoutDashboard,
  Cpu,
  Wrench,
  BarChart3,
  Bell,
  FileText,
  HelpCircle
} from 'lucide-react';

// Type for dashboard sections
type DashboardSection = {
  id: string;
  label: string;
  icon: React.ReactNode;
};

// Dashboard sections array
const dashboardSections: DashboardSection[] = [
  {
    id: 'overview',
    label: 'Resumen',
    icon: <LayoutDashboard className="h-5 w-5" />
  },
  {
    id: 'machines',
    label: 'Mis máquinas',
    icon: <Cpu className="h-5 w-5" />
  },
  {
    id: 'spare-parts',
    label: 'Recambios',
    icon: <Wrench className="h-5 w-5" />
  },
  {
    id: 'analytics',
    label: 'Análisis',
    icon: <BarChart3 className="h-5 w-5" />
  },
  {
    id: 'notifications',
    label: 'Notificaciones',
    icon: <Bell className="h-5 w-5" />
  },
  {
    id: 'documentation',
    label: 'Documentación',
    icon: <FileText className="h-5 w-5" />
  },
  {
    id: 'support',
    label: 'Soporte',
    icon: <HelpCircle className="h-5 w-5" />
  }
];

export default function UserDashboard() {
  const [activeSection, setActiveSection] = useState<string>('overview');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userData, setUserData] = useState<any>(null);
  const [_, navigate] = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const isAuthenticated = authService.isAuthenticated();
        if (!isAuthenticated) {
          navigate('/login');
          return;
        }

        // Get user data
        const user = authService.getCurrentUser();
        setUserData(user);
        setIsLoading(false);
      } catch (error) {
        console.error('Error al verificar autenticación:', error);
        navigate('/login');
      }
    };

    checkAuth();
  }, [navigate]);

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'overview':
        return <DashboardOverview userData={userData} />;
      case 'machines':
        return <UserMachines userData={userData} />;
      case 'spare-parts':
        return <SpareParts userData={userData} />;
      case 'analytics':
        return <AnalyticsDashboard userData={userData} />;
      case 'notifications':
        return <NotificationCenter userData={userData} />;
      case 'documentation':
        return <DocumentationLibrary userData={userData} />;
      case 'support':
        return <SupportCenter userData={userData} />;
      default:
        return <DashboardOverview userData={userData} />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-zinc-900 text-white">
      {/* Side Navigation */}
      <div className="w-64 border-r border-zinc-800 overflow-y-auto">
        <DashboardNavigation
          sections={dashboardSections}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />

        {/* Botón para volver al sitio principal */}
        <div className="p-4 mt-4">
          <Button
            variant="outline"
            className="w-full border-white/10 hover:bg-zinc-800 flex items-center justify-center gap-2"
            onClick={() => navigate('/')}
          >
            <Home className="h-4 w-4" />
            <span>Volver al sitio</span>
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Header */}
        <DashboardHeader userData={userData} />

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-6">
          {renderActiveSection()}
        </main>
      </div>
    </div>
  );
}
